/**
 * Компонент третьего шага - выбор блюд из меню
 *
 * Отображает:
 * - Информацию о применённом промокоде (если есть)
 * - Сетку карточек блюд
 * - Нижнюю панель с суммой заказа
 * - Модальное окно о недействительном промокоде
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ORDER_SOURCES } from '../../../config/sourceFields';
import { Modal } from '../../../components/UI/Modal';
import { DishCard } from './DishCard';
import { PromoInfoBanner } from './PromoInfoBanner';
import { OrderBottomBar } from './OrderBottomBar';
import { DishesSelectionStepProps } from '../types';

export const DishesSelectionStep: React.FC<DishesSelectionStepProps> = ({
  selectedSource,
  menuData,
  selectedDishes,
  onBack,
  onDishClick,
  onOpenOrderDetails,
  calculateFinalPrice,
  calculateTotal,
}) => {
  const { t, language } = useLanguage();

  // Состояние модалки недействительного промокода
  const [showInvalidPromoModal, setShowInvalidPromoModal] = useState(false);

  // Получаем название источника
  const sourceLabel = ORDER_SOURCES.find((s) => s.value === selectedSource);
  const sourceLabelText = sourceLabel
    ? language === 'ru'
      ? sourceLabel.label_ru
      : sourceLabel.label_en
    : '';

  return (
    <div className="min-h-screen bg-background-100 py-8">
      <div className="container-app max-w-6xl">
        {/* Заголовок */}
        <div className="mb-6">
          {/* Кнопка "Назад" */}
          <button
            onClick={onBack}
            className="flex items-center text-text-secondary hover:text-primary-500 transition-colors mb-4"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M15 19l-7-7 7-7" />
            </svg>
            {t('addOrder.backToFields')}
          </button>

          <h1 className="text-2xl font-bold text-text-primary mb-2">
            {t('addOrder.selectDishesTitle')}: {sourceLabelText}
          </h1>
          <p className="text-text-secondary">{t('addOrder.selectDishesSubtitle')}</p>

          {/* Баннер с информацией о промокоде */}
          <PromoInfoBanner promo={menuData.promo} />
        </div>

        {/* Список блюд */}
        <div className="pb-32">
          {menuData.dishes.length === 0 ? (
            // Пустое меню
            <div className="card text-center py-12">
              <p className="text-text-secondary">{t('addOrder.noDishesAvailable')}</p>
            </div>
          ) : (
            // Сетка карточек блюд
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {menuData.dishes.map((dish) => {
                // Находим выбранные порции для этого блюда
                const selectedPortionsForDish = selectedDishes.filter(
                  (item) => item.dishId === dish.id
                );

                return (
                  <DishCard
                    key={dish.id}
                    dish={dish}
                    selectedPortions={selectedPortionsForDish}
                    promoInfo={menuData.promo}
                    onClick={() => onDishClick(dish)}
                    calculateFinalPrice={calculateFinalPrice}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Нижняя панель с суммой заказа */}
        <OrderBottomBar
          selectedDishes={selectedDishes}
          total={calculateTotal()}
          onOpenDetails={onOpenOrderDetails}
        />
      </div>

      {/* Модальное окно о недействительном промокоде */}
      <Modal
        isOpen={showInvalidPromoModal}
        onClose={() => setShowInvalidPromoModal(false)}
        title={t('addOrder.promoInvalidTitle')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">{t('addOrder.promoInvalidMessage')}</p>
          <div className="flex justify-end">
            <button
              onClick={() => setShowInvalidPromoModal(false)}
              className="btn btn-primary"
            >
              {t('addOrder.okay')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
